import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import { API_URLS, API_BASE_URL } from '../../constants/api-urls';
import './InspectionRFQDetails.scss';

const API_BASE_URL_MEDIA = API_BASE_URL.replace('/api/v1', '');

// Helper to get full image/video URL from access path
const getMediaUrl = (accessPath, auctionId = null) => {
  if (!accessPath) return '';
  if (accessPath.startsWith('http://') || accessPath.startsWith('https://')) return accessPath;
  const baseUrl = API_BASE_URL_MEDIA;
  if (accessPath.includes('C:/usr/local/b2b/seller/AuctionProduct') || accessPath.includes('C:\\usr\\local\\b2b\\seller\\AuctionProduct')) {
    const match = accessPath.match(/AuctionProduct[\/\\](\d+)[\/\\]([^\/\\]+)$/);
    if (match) return `${baseUrl}/auction-media/${match[1]}/${match[2]}`;
    const tempMatch = accessPath.match(/AuctionProduct[\/\\]temp[\/\\]([^\/\\]+)$/);
    if (tempMatch) return `${baseUrl}/auction-media/temp/${tempMatch[1]}`;
  }
  if (accessPath.startsWith('/auction-media/')) return `${baseUrl}${accessPath}`;
  let cleanPath = accessPath;
  if (cleanPath.startsWith(':3000')) cleanPath = cleanPath.replace(/^:3000/, '');
  if (!cleanPath.startsWith('/')) cleanPath = '/' + cleanPath;
  if (!cleanPath.startsWith('/auction-media')) {
    const fileName = cleanPath.split('/').pop();
    if (fileName && auctionId) return `${baseUrl}/auction-media/${auctionId}/${fileName}`;
    return `${baseUrl}/media/${fileName}`;
  }
  return `${baseUrl}${cleanPath}`;
};

// Format address from JSON string/object to readable text
const formatAddress = (raw) => {
  if (raw == null || raw === '') return 'N/A';
  try {
    const addr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (typeof addr !== 'object') return String(raw);
    const parts = [
      addr.address || addr.full_address || '',
      addr.city || '',
      addr.state || '',
      addr.country || '',
      addr.pincode ? `- ${addr.pincode}` : ''
    ].filter(Boolean);
    return parts.length ? parts.join(', ') : 'N/A';
  } catch {
    return typeof raw === 'string' ? raw : 'N/A';
  }
};

export default function InspectionRFQDetails() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [auction, setAuction] = useState(null);
  const [winningBid, setWinningBid] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existingQuotation, setExistingQuotation] = useState(null);
  
  const [formData, setFormData] = useState({
    quote_amount: 5000,
    proposed_date: '',
    proposed_time: '10:00',
    estimated_duration_hours: '2-3 hours',
    inspection_approach: '',
    additional_notes: ''
  });

  useEffect(() => {
    fetchRFQDetails();
  }, [rfqId]);

  const fetchRFQDetails = async () => {
    try {
      setLoading(true);
      const response = await CommonService.get(API_URLS.AUCTION.GET_INSPECTION_RFQ(rfqId));
      if (response && response.success) {
        setRfq(response.data);
        
        // Pre-fill proposed date/time from RFQ preferred date/time
        if (response.data.preferred_date) {
          const preferredDate = new Date(response.data.preferred_date);
          setFormData(prev => ({
            ...prev,
            proposed_date: preferredDate.toISOString().split('T')[0],
            proposed_time: response.data.preferred_time || '10:00'
          }));
        }
        
        // Fetch auction details
        const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${response.data.auction_id}`);
        if (auctionResponse && auctionResponse.success) {
          setAuction(auctionResponse.data);
          
          // Fetch winning bid to get bid amount
          if (response.data.winning_bid_id) {
            try {
              const bidResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${response.data.auction_id}/bids`);
              if (bidResponse && bidResponse.success && Array.isArray(bidResponse.data)) {
                const bid = bidResponse.data.find(b => b.bid_id === response.data.winning_bid_id);
                if (bid) {
                  setWinningBid(bid);
                }
              }
            } catch (error) {
              console.error('Error fetching winning bid:', error);
            }
          }
          
          // Fetch seller profile to get seller's location
          if (auctionResponse.data.user_login_id) {
            try {
              const sellerResponse = await CommonService.get(`/company/profile/${auctionResponse.data.user_login_id}`);
              if (sellerResponse && sellerResponse.success) {
                setSellerProfile(sellerResponse.data);
              }
            } catch (error) {
              console.error('Error fetching seller profile:', error);
            }
          }
        }
      }

      try {
        const quoteResponse = await CommonService.get(API_URLS.AUCTION.GET_MY_INSPECTION_QUOTATION(rfqId));
        if (quoteResponse?.success && quoteResponse.data) {
          setExistingQuotation(quoteResponse.data);
        } else {
          setExistingQuotation(null);
        }
      } catch (quoteErr) {
        console.warn('Could not load existing quotation', quoteErr);
        setExistingQuotation(null);
      }
    } catch (error) {
      console.error('Error fetching RFQ:', error);
      toast.error('Failed to load RFQ details');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.quote_amount || formData.quote_amount < 500) {
      toast.error('Quote amount must be at least ₹500');
      return;
    }

    if (!formData.proposed_date || !formData.proposed_time) {
      toast.error('Please select proposed date and time');
      return;
    }

    if (!formData.inspection_approach || formData.inspection_approach.length < 50) {
      toast.error('Inspection approach must be at least 50 characters');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        rfq_id: parseInt(rfqId),
        quote_amount: parseFloat(formData.quote_amount),
        proposed_date: formData.proposed_date,
        proposed_time: formData.proposed_time,
        estimated_duration_hours: formData.estimated_duration_hours,
        inspection_approach: formData.inspection_approach,
        additional_notes: formData.additional_notes || null
      };

      const response = await CommonService.post(API_URLS.AUCTION.CREATE_INSPECTION_QUOTATION, payload);
      
      if (response && response.success) {
        const pendingApproval = response.data?.approval?.mode === 'pending';
        toast.success(
          pendingApproval
            ? 'Quotation submitted and sent for internal approval. The buyer will see it after approval.'
            : 'Quotation submitted successfully!',
        );
        navigate('/auction/qa/dashboard');
      } else {
        throw new Error(response?.message || 'Failed to submit quotation');
      }
    } catch (error) {
      console.error('Error submitting quotation:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit quotation');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
  };

  const getQuotationStatusMeta = (quotation) => {
    if (!quotation) return { label: 'Submitted', className: 'badge-submitted' };
    const approval = String(quotation.quote_approval_status || '').trim();
    const status = String(quotation.status || '').trim();
    if (approval === 'pending' || status === 'pending_approval') {
      return { label: 'Pending internal approval', className: 'badge-pending' };
    }
    if (approval === 'rejected' || status === 'rejected') {
      return { label: 'Rejected', className: 'badge-rejected' };
    }
    if (status === 'selected') {
      return { label: 'Selected by buyer', className: 'badge-selected' };
    }
    if (approval === 'approved') {
      return { label: 'Approved — visible to buyer', className: 'badge-approved' };
    }
    return { label: 'Submitted — visible to buyer', className: 'badge-submitted' };
  };

  const formatProposedDate = (quotation) => {
    const raw = quotation?.proposed_date || quotation?.proposed_start_date;
    if (!raw) return '—';
    return formatDate(raw);
  };

  const formatProposedTime = (quotation) => {
    const raw = quotation?.proposed_time;
    if (!raw) return '—';
    return String(raw).substring(0, 5);
  };

  if (loading) {
    return <div className="inspection-rfq-details loading">Loading...</div>;
  }

  if (!rfq || !auction) {
    return <div className="inspection-rfq-details error">RFQ or auction not found</div>;
  }

  const getProductImages = () => {
    if (!auction?.images) return [];
    if (Array.isArray(auction.images)) return auction.images;
    if (typeof auction.images === 'string') {
      try {
        const parsed = JSON.parse(auction.images);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        return [auction.images];
      }
    }
    return [];
  };
  const productImages = getProductImages();
  const mainImageUrl = productImages[0] ? getMediaUrl(productImages[0], auction?.auction_id) : null;

  const getSellerLocation = () => {
    let address = '', city = '', state = '';
    if (sellerProfile?.address) {
      try {
        const a = typeof sellerProfile.address === 'string' ? JSON.parse(sellerProfile.address) : sellerProfile.address;
        address = a.address || a.full_address || '';
        city = a.city || '';
        state = a.state || '';
      } catch (_) {}
    }
    if (!address && auction) {
      address = auction.address || '';
      city = auction.city || '';
      state = auction.state || '';
    }
    return { address, city, state };
  };
  const sellerLocation = getSellerLocation();
  const buyerCompany = rfq.buyer_company || winningBid?.bidder_company || 'N/A';
  const buyerName = rfq.buyer_name || winningBid?.bidder_name || 'N/A';
  const buyerEmail = rfq.buyer_email || winningBid?.bidder_email;
  const buyerPhone = rfq.buyer_phone || winningBid?.bidder_phone;
  const buyerLocation = formatAddress(rfq.buyer_address || winningBid?.bidder_address);
  const sellerCompany = auction?.seller_company || auction?.seller_name || 'N/A';
  const sellerName = auction?.seller_name || 'N/A';
  const sellerEmail = auction?.seller_email || sellerProfile?.user_email;
  const sellerPhone = auction?.seller_phone || sellerProfile?.user_phone;
  const sellerLocationStr = [auction?.city, auction?.state].filter(Boolean).join(', ') || formatAddress(sellerProfile?.address);

  return (
    <div className="inspection-rfq-details">
      <div className="container-fluid">
        {/* Header: title + subtitle left, RFQ badge right */}
        <div className="page-header">
          <button className="back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <div className="header-content">
            <h1>
              <span className="title-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12h6M9 16h6M9 8h6M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </span>
              Inspection RFQ Details
            </h1>
            <p>{existingQuotation ? 'Your submitted quotation for this request' : 'Review complete information and submit your quotation'}</p>
          </div>
          <div className="rfq-badge">
            <div className="rfq-id">RFQ ID: {rfq.rfq_number || rfqId}</div>
            <div className="rfq-date">Requested: {formatDateShort(rfq.created_at)}</div>
          </div>
        </div>

        <div className="content-layout">
          {/* Left Column */}
          <div className="left-column">
            {/* Product to be Inspected - image left, content right */}
            <div className="section-card product-card">
              <div className="section-header">
                <div className="section-icon box">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2 6h16v10H2V6zM2 6l2-4h12l2 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Product to be Inspected</h3>
              </div>
              <div className="product-overview">
                <div className="product-image-main">
                  {mainImageUrl ? (
                    <img src={mainImageUrl} alt={auction.product_name} onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.classList.remove('hidden'); }} />
                  ) : null}
                  <div className={`placeholder-image ${mainImageUrl ? 'hidden' : ''}`}>No Image</div>
                </div>
                <div className="product-content">
                  <div className="product-name">{auction.product_name}</div>
                  <div className="product-description">{auction.product_description || 'No description available'}</div>
                  <div className="product-attributes grid">
                    <div className="attribute-item">
                      <span className="label">Category:</span>
                      <span className="value">{auction.category_name || 'N/A'}</span>
                    </div>
                    {auction.product_attributes && (() => {
                      const attrs = typeof auction.product_attributes === 'string' ? JSON.parse(auction.product_attributes) : auction.product_attributes;
                      return (
                        <>
                          {attrs.brand && (
                            <div className="attribute-item">
                              <span className="label">Brand:</span>
                              <span className="value">{attrs.brand}</span>
                            </div>
                          )}
                          {attrs.condition && (
                            <div className="attribute-item">
                              <span className="label">Condition:</span>
                              <span className="value">{attrs.condition}</span>
                            </div>
                          )}
                          {attrs.year && (
                            <div className="attribute-item">
                              <span className="label">Year:</span>
                              <span className="value">{attrs.year}</span>
                            </div>
                          )}
                          {attrs.fuel_type && (
                            <div className="attribute-item">
                              <span className="label">Fuel Type:</span>
                              <span className="value">{attrs.fuel_type}</span>
                            </div>
                          )}
                          {attrs.transmission && (
                            <div className="attribute-item">
                              <span className="label">Transmission:</span>
                              <span className="value">{attrs.transmission}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div className="attribute-item">
                      <span className="label">Quantity:</span>
                      <span className="value">{auction.quantity || 1} Unit</span>
                    </div>
                    <div className="attribute-item highlight">
                      <span className="label">Final Bid Amount:</span>
                      <span className="value">{formatCurrency(winningBid?.bid_amount || rfq.winning_bid_amount || auction.winning_bid_amount || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
              {productImages.length > 0 && (
                <div className="product-images-subsection">
                  <div className="subsection-label">Product Images ({productImages.length})</div>
                  <div className="product-thumbnails">
                    {productImages.map((img, i) => (
                      <div key={i} className="thumb">
                        <img src={getMediaUrl(img, auction.auction_id)} alt="" onError={(e) => e.target.style.display = 'none'} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Inspection Location + Preferred Schedule side by side */}
            <div className="two-cards-row">
              <div className="section-card location-card">
                <div className="section-header">
                  <div className="section-icon green">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 1C6.24 1 4 3.24 4 6C4 10.5 10 17 10 17C10 17 16 10.5 16 6C16 3.24 13.76 1 10 1ZM10 8.25C8.76 8.25 7.75 7.24 7.75 6C7.75 4.76 8.76 3.75 10 3.75C11.24 3.75 12.25 4.76 12.25 6C12.25 7.24 11.24 8.25 10 8.25Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <h3>Inspection Location</h3>
                </div>
                <div className="location-address">{sellerLocation.address || 'Address not available'}</div>
                <div className="location-city-state">{[sellerLocation.city, sellerLocation.state].filter(Boolean).join(', ')}</div>
                <div className="access-label">Access Instructions</div>
                <div className="access-instructions">{rfq.special_instructions ? 'See special instructions below' : 'Buyer arranges collection'}</div>
              </div>

              <div className="section-card schedule-card">
                <div className="section-header">
                  <div className="section-icon blue">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <h3>Preferred Schedule</h3>
                </div>
                <div className="schedule-item">
                  <span className="label">Preferred Date:</span>
                  <span className="value">{formatDate(rfq.preferred_date)}</span>
                </div>
                <div className="schedule-item">
                  <span className="label">Preferred Time:</span>
                  <span className="value">{rfq.preferred_time || '10:00'}</span>
                </div>
                <div className="sla-pill">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                  <span>72-hour SLA for completion</span>
                </div>
              </div>
            </div>

            {/* Seller + Buyer side by side */}
            <div className="two-cards-row">
              <div className="section-card seller-card">
                <div className="section-header">
                  <div className="section-icon person">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 10c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z" fill="currentColor"/>
                    </svg>
                  </div>
                  <h3>Seller Information</h3>
                </div>
                <div className="info-item">
                  <span className="label">Company:</span>
                  <span className="value">{sellerCompany}</span>
                </div>
                <div className="info-item">
                  <span className="label">Contact:</span>
                  <span className="value">{sellerName}</span>
                </div>
                <div className="contact-details">
                  {sellerPhone && (
                    <a href={`tel:${sellerPhone}`} className="contact-link">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                      {sellerPhone}
                    </a>
                  )}
                  {sellerEmail && (
                    <a href={`mailto:${sellerEmail}`} className="contact-link">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
                      {sellerEmail}
                    </a>
                  )}
                  <div className="contact-link location">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {sellerLocationStr || 'N/A'}
                  </div>
                </div>
              </div>

              <div className="section-card buyer-card">
                <div className="section-header">
                  <div className="section-icon person">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 10c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z" fill="currentColor"/>
                    </svg>
                  </div>
                  <h3>Buyer Information</h3>
                </div>
                <div className="info-item">
                  <span className="label">Company:</span>
                  <span className="value">{buyerCompany}</span>
                </div>
                <div className="info-item">
                  <span className="label">Contact:</span>
                  <span className="value">{buyerName}</span>
                </div>
                <div className="contact-details">
                  {buyerPhone && (
                    <a href={`tel:${buyerPhone}`} className="contact-link">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                      {buyerPhone}
                    </a>
                  )}
                  {buyerEmail && (
                    <a href={`mailto:${buyerEmail}`} className="contact-link">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
                      {buyerEmail}
                    </a>
                  )}
                  <div className="contact-link location">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    {buyerLocation}
                  </div>
                </div>
              </div>
            </div>

            {/* Primary Contact for Inspection */}
            <div className="section-card primary-contact-card">
              <div className="section-header">
                <div className="section-icon person">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 10c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z" fill="currentColor"/>
                  </svg>
                </div>
                <h3>Primary Contact for Inspection</h3>
              </div>
              <div className="primary-contact-item">
                <span className="label">Requested by:</span>
                <span className="value">{buyerCompany} (Buyer)</span>
              </div>
              <div className="primary-contact-item">
                <span className="label">Contact Person:</span>
                <span className="value">{buyerName}</span>
              </div>
              <div className="contact-details">
                {buyerPhone && (
                  <a href={`tel:${buyerPhone}`} className="contact-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                    {buyerPhone}
                  </a>
                )}
                {buyerEmail && (
                  <a href={`mailto:${buyerEmail}`} className="contact-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><path d="M22 6l-10 7L2 6"/></svg>
                    {buyerEmail}
                  </a>
                )}
              </div>
            </div>

            {/* Required Inspection Checklist */}
            <div className="section-card checklist-card">
              <div className="section-header">
                <div className="section-icon green">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Required Inspection Checklist</h3>
              </div>
              <div className="checklist-items">
                {(() => {
                  let items = [];
                  try {
                    const raw = rfq.inspection_checklist;
                    if (raw) items = Array.isArray(raw) ? raw : (typeof raw === 'string' ? JSON.parse(raw) : []);
                    if (!Array.isArray(items)) items = [];
                  } catch (_) {}
                  return items.map((item, index) => (
                  <div key={index} className="checklist-item">
                    <div className="check-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <span>{item}</span>
                  </div>
                ));
                })()}
              </div>
            </div>

            {/* Special Instructions - full width, yellow */}
            {(rfq.special_instructions) && (
              <div className="section-card instructions-card">
                <div className="section-header">
                  <div className="section-icon yellow">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 1l8 16H2L10 1z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M10 7v4M10 13h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3>Special Instructions</h3>
                </div>
                <p>{rfq.special_instructions}</p>
              </div>
            )}
          </div>

          {/* Right Column - Submit or View Quotation */}
          <div className="right-column">
            {existingQuotation ? (
              <div className="quotation-form-card quotation-view-card">
                <div className="section-header">
                  <div className="section-icon purple">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
                    </svg>
                  </div>
                  <h3>Your Quotation</h3>
                </div>

                {(() => {
                  const statusMeta = getQuotationStatusMeta(existingQuotation);
                  return (
                    <div className={`quotation-status-pill ${statusMeta.className}`}>
                      {statusMeta.label}
                    </div>
                  );
                })()}

                <div className="quotation-detail-grid">
                  <div className="detail-row">
                    <span className="detail-label">Quote Amount</span>
                    <span className="detail-value">{formatCurrency(existingQuotation.quote_amount)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Proposed Date</span>
                    <span className="detail-value">{formatProposedDate(existingQuotation)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Proposed Time</span>
                    <span className="detail-value">{formatProposedTime(existingQuotation)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Estimated Duration</span>
                    <span className="detail-value">{existingQuotation.estimated_duration_hours || '—'}</span>
                  </div>
                  <div className="detail-row full-width">
                    <span className="detail-label">Inspection Approach</span>
                    <span className="detail-value">{existingQuotation.inspection_approach || '—'}</span>
                  </div>
                  {existingQuotation.additional_notes && (
                    <div className="detail-row full-width">
                      <span className="detail-label">Additional Notes</span>
                      <span className="detail-value">{existingQuotation.additional_notes}</span>
                    </div>
                  )}
                  {existingQuotation.submitted_at && (
                    <div className="detail-row">
                      <span className="detail-label">Submitted On</span>
                      <span className="detail-value">{formatDateShort(existingQuotation.submitted_at)}</span>
                    </div>
                  )}
                </div>

                <div className="info-text">
                  {existingQuotation.quote_approval_status === 'pending' || existingQuotation.status === 'pending_approval'
                    ? 'Your quotation is awaiting internal approval. The buyer will be able to review it once approved.'
                    : existingQuotation.status === 'selected'
                      ? 'The buyer has selected your quotation. Check My Inspections to proceed.'
                      : 'You have already submitted a quotation for this request. The buyer can review and accept it.'}
                </div>

                <button
                  type="button"
                  className="btn-submit btn-secondary-outline"
                  onClick={() => navigate('/auction/qa/dashboard/requests?filter=quoted')}
                >
                  Back to My Quotations
                </button>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="quotation-form-card">
              <div className="section-header">
                <div className="section-icon purple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                  </svg>
                </div>
                <h3>Submit Your Quotation</h3>
              </div>

              <div className="form-group">
                <label>Quote Amount (₹) <span className="required">*</span></label>
                <div className="input-with-prefix">
                  <span className="prefix">₹</span>
                  <input
                    type="number"
                    className="form-control"
                    name="quote_amount"
                    value={formData.quote_amount}
                    onChange={handleInputChange}
                    min="500"
                    required
                  />
                </div>
                <div className="form-hint">Minimum: ₹500</div>
              </div>

              <div className="form-group">
                <label>Proposed Date <span className="required">*</span></label>
                <input
                  type="date"
                  className="form-control"
                  name="proposed_date"
                  value={formData.proposed_date}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Proposed Time <span className="required">*</span></label>
                <input
                  type="time"
                  className="form-control"
                  name="proposed_time"
                  value={formData.proposed_time}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Estimated Duration (hours)</label>
                <div className="input-with-suffix select-wrap">
                  <select
                    className="form-control"
                    name="estimated_duration_hours"
                    value={formData.estimated_duration_hours}
                    onChange={handleInputChange}
                  >
                    <option value="1-2 hours">1-2 hours</option>
                    <option value="2-3 hours">2-3 hours</option>
                    <option value="3-4 hours">3-4 hours</option>
                    <option value="4-6 hours">4-6 hours</option>
                    <option value="6+ hours">6+ hours</option>
                  </select>
                  <span className="suffix-icon arrow">▼</span>
                </div>
              </div>

              <div className="form-group">
                <label>Inspection Approach <span className="required">*</span></label>
                <textarea
                  className="form-control"
                  name="inspection_approach"
                  rows="5"
                  placeholder="Describe your inspection methodology, tools, and approach..."
                  value={formData.inspection_approach}
                  onChange={handleInputChange}
                  required
                  minLength={50}
                />
                <div className="form-hint">Minimum 50 characters</div>
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  className="form-control"
                  name="additional_notes"
                  rows="3"
                  placeholder="Any additional information or requirements..."
                  value={formData.additional_notes}
                  onChange={handleInputChange}
                />
              </div>

              <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Quotation'}
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="info-text">
                Your quote will be sent to the requester for review. You'll be notified if accepted.
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
